import { Association } from '@/models/associations/association';
import { Business } from '@/models/businesses/business';
import { BusinessOrder } from '@/models/orders/business-order';
import { Reference } from '@/models/references/reference';
import { UserPermissionAction } from '@/models/users/user-permissions/user-entity-permissions';
import { authorizationManagerService } from '@/services';

const routes = [
    {
        path: '/',
        name: 'home',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "home" */ './views/HomeView.vue'),
        meta: { isPublic: true }
    },
    {
        path: '/authentication-callback',
        name: 'authenticationCallback',
        meta: { isPublic: true }
    },
    {
        path: '/authentication-silent-renew',
        name: 'authenticationSilentRenew',
        meta: { isPublic: true }
    },
    {
        path: '/logout',
        name: 'logout',
        meta: { isPublic: true }
    },
    {
        path: '/info/terms-of-service',
        name: 'termsOfService',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "info" */ './views/info/TermsOfServiceView.vue'),
        meta: { isPublic: true }
    },
    {
        path: '/info/install',
        name: 'installPWA',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "info" */ './views/info/InstallPWAView.vue'),
        meta: { isPublic: true }
    },
    {
        path: '/info/privacy-policy',
        name: 'privacyPolicy',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "info" */ './views/info/PrivacyPolicyView.vue'),
        meta: { isPublic: true }
    },
    {
        path: '/errors/401',
        name: '401',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "errors" */ './views/errors/Error401UnauthorizedView.vue'),
        meta: { isPublic: true }
    },
    {
        path: '/errors/403',
        name: '403',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "errors" */ './views/errors/Error403ForbiddenView.vue'),
        meta: { isPublic: true }
    },
    {
        path: '/errors/404',
        name: '404',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "errors" */ './views/errors/Error404NotFoundView.vue'),
        meta: { isPublic: true }
    },
    {
        path: '/dashboard',
        name: 'dashboard',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "dashboard" */ './views/DashboardView.vue'),
        meta: { isAuthorized: () => true }
    },
    {
        path: '/users/invitations',
        name: 'userInvitations',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "users" */ './views/users/UserInvitationsListView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canListUserInvitations }
    },
    {
        path: '/users/invitations/new',
        name: 'newUserInvitation',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "users" */ './views/users/NewUserInvitationView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canCreateUserInvitations }
    },
    {
        path: '/users/action-logs',
        name: 'userActionLogs',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "users" */ './views/users/UserActionLogsListView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.isAdmin }
    },
    {
        path: '/businesses/new',
        name: 'newBusiness',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/NewBusinessView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canCreateBusiness }
    },
    {
        path: '/businesses/new-online',
        name: 'newBusinessProspect',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/prospects/NewBusinessProspectView.vue'),
        meta: { isPublic: true }
    },
    {
        path: '/businesses/prospects',
        name: 'businessProspects',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/prospects/BusinessProspectsListView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canListBusinessProspects }
    },
    {
        path: '/businesses',
        name: 'businesses',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessListView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canListBusiness }
    },
    {
        path: '/businesses/dashboard/:id',
        name: 'businessDashboard',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessDashboardView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/basic-data/:id',
        name: 'businessBasicData',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessBasicDataView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/front-page/:id',
        name: 'businessFrontpage',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessFrontPageView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/front-page/:id/custom-highlight/new',
        name: 'newBusinessFrontpageCustomHighlight',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/EditBusinessCustomHighlightsView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Write, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/front-page/:id/custom-highlight/:customHighlightId',
        name: 'editBusinessFrontpageCustomHighlight',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/EditBusinessCustomHighlightsView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Write, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/front-page/:id/image-and-titles',
        name: 'editBusinessFrontpageImageAndTitles',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/EditBusinessImageAndTitlesView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Write, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/payment-options/:id',
        name: 'businessPaymentOptions',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessPaymentOptionsView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/kpis/:id',
        name: 'businessKpis',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/kpis/BusinessKpisView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/logistics/:id',
        name: 'businessLogistics',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessLogisticsView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/subscription/:id',
        name: 'businessSubscription',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessSubscriptionView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/logistics/:id/shipping-rate/new',
        name: 'newBusinessLogisticsShippingRate',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/EditBusinessLogisticsShippingRateView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Write, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/logistics/:id/shipping-rate/:shippingRateId',
        name: 'editBusinessLogisticsShippingRate',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/EditBusinessLogisticsShippingRateView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Write, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/booking-options/:id',
        name: 'businessBookingOptions',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessBookingOptionsView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/contact-settings/:id',
        name: 'businessContactSettings',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessContactSettingsView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/references-classification/:id',
        name: 'businessReferencesClassification',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessReferencesClassificationView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/references-classification/attributes/:id',
        name: 'businessReferenceAttributes',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessAttributesView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/references-classification/categories/:id',
        name: 'businessReferenceCategories',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessCategoriesView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/references-classification/characteristics/:id',
        name: 'businessReferenceCharacteristics',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessCharacteristicsView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/businesses/selling/:id',
        name: 'businessSellingOptions',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/BusinessSellingView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Business({ id: +params.id })) }
    },
    {
        path: '/private/businesses-geolocation-task',
        name: 'businessesGeolocationTask',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "private" */ './views/businesses/BusinessesGeolocationTaskView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.isAdmin }
    },
    {
        path: '/associations/new',
        name: 'newAssociation',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "associations" */ './views/associations/NewAssociationView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canCreateAssociations }
    },
    {
        path: '/associations',
        name: 'associations',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "associations" */ './views/associations/AssociationsListView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canListAssociations }
    },
    {
        path: '/associations/dashboard/:id',
        name: 'associationDashboard',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "associations" */ './views/associations/AssociationDashboardView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Association({ id: +params.id })) }
    },
    {
        path: '/associations/front-page/:id',
        name: 'associationFrontpage',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "associations" */ './views/associations/AssociationFrontPageView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Association({ id: +params.id })) }
    },
    {
        path: '/products/discounts',
        name: 'productDiscounts',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "products" */ './views/products/ApplyProductDiscountsView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canListReferences }
    },
    {
        path: '/products/:id',
        name: 'editProduct',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "products" */ './views/products/EditProductView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new Reference({ id: +params.id })) }
    },
    {
        path: '/products',
        name: 'products',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "products" */ './views/products/ProductsListView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canListReferences }
    },
    {
        path: '/products/:id/edit-skus',
        name: 'editProductSkus',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "products" */ './views/products/EditProductSkusView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Write, new Reference({ id: +params.id })) }
    },
    {
        path: '/products/:id/generate-skus/:categoryId',
        name: 'generateProductSkus',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "products" */ './views/products/GenerateProductSkusView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Write, new Reference({ id: +params.id })) }
    },
    {
        path: '/services',
        name: 'services',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "services" */ './views/services/ServicesListView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canListReferences }
    },
    {
        path: '/orders',
        name: 'orders',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "orders" */ './views/orders/BusinessOrdersListView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canListBusinessOrders }
    },
    {
        path: '/orders/:id',
        name: 'viewBusinessOrder',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "orders" */ './views/orders/BusinessOrderDetailsView.vue'),
        meta: { isAuthorized: (params: any) => authorizationManagerService.can(UserPermissionAction.Read, new BusinessOrder({ id: +params.id })) }
    },
    {
        path: '/booking-requests',
        name: 'bookingRequests',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "bookings" */ './views/bookings/BusinessBookingRequestsListView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canListBusinessBookingRequests }
    },
    {
        path: '/contacts',
        name: 'contacts',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "contacts" */ './views/contacts/BusinessContactsListView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canListBusinessContacts }
    },
    {
        path: '/profiles/new',
        name: 'newProfile',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "businesses" */ './views/businesses/NewProfileView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canCreateBusiness }
    },
    {
        path: '/surveys/campaign/:id',
        name: 'surveyForCampaign',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "surveys" */ './views/surveys/CampaignSurveyView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canListCampaigns }
    },
    {
        path: '/surveys/campaign/:id/results',
        name: 'surveyResultsForCampaign',
        // @ts-ignore
        component: () => import(/* webpackChunkName: "surveys" */ './views/surveys/CampaignSurveyResultsView.vue'),
        meta: { isAuthorized: () => authorizationManagerService.canListCampaigns }
    },
    // otherwise redirect to home
    { path: '*', redirect: '/' }
];

export default routes;
